import React, { useState } from 'react';
import { Box, Typography, Button, Paper, alpha, useTheme, CircularProgress } from '@mui/material';
import { ArrowForward, Close, AccountBalance, FlashOn } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../login/brand';
import { DASH } from './dashboardColors';

const CrossSellBanner = ({ 
  currentType = 'fastFunding', // Show opposite type as cross-sell
  onDismiss,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  const isSellingSBA = currentType === 'fastFunding';

  const content = isSellingSBA ? {
    icon: <AccountBalance sx={{ fontSize: 34, color: DASH.brown }} />,
    title: t('dashboard.wantSbaAccess'),
    subtitle: t('dashboard.completePfsToUnlock'),
    buttonText: t('dashboard.startSbaApplication'),
  } : {
    icon: <FlashOn sx={{ fontSize: 34, color: DASH.accent }} />,
    title: t('dashboard.needFundingFaster'),
    subtitle: t('dashboard.applyFastFunding'),
    buttonText: t('dashboard.getFastFunding'),
  };
  content.gradient = `linear-gradient(135deg, ${DASH.cream} 0%, ${DASH.creamDeep} 100%)`;
  content.borderColor = DASH.accent;

  const handleClick = async () => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const appType = isSellingSBA ? 'pfs' : 'financing';
      const baseUrl = isSellingSBA 
        ? (import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com')
        : (import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com');
      
      // Build redirect URL
      const url = new URL(baseUrl);
      const returnParam = isSellingSBA ? 'pfs_submitted=true' : 'submitted=true';
      const returnUrl = `${import.meta.env.VITE_CORELOANHUB_URL || 'http://localhost:3000'}/dashboard?${returnParam}&source=${appType === 'pfs' ? 'pfs-copilot' : 'financing-app'}`;
      url.searchParams.append('returnUrl', returnUrl);
      
      if (user?.email) {
        url.searchParams.append('email', user.email);
      }
      
      // Generate auth token
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/auth/generate-single-use-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        url.searchParams.append('authToken', response.data.token);
      }
      
      window.location.href = url.toString();
    } catch (error) {
      console.error('Failed to generate redirect:', error);
      setIsLoading(false);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Minimized compact view - always visible
  if (isMinimized) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          background: content.gradient,
          border: `1px solid ${alpha(content.borderColor, 0.2)}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'all 0.2s ease',
          '&:hover': { borderColor: alpha(content.borderColor, 0.5) },
        }}
        onClick={handleMinimize}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {content.icon}
          <Typography variant="body2" sx={{ fontWeight: 600, color: content.borderColor }}>
            {content.title}
          </Typography>
        </Box>
        <Button
          size="small"
          endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
          sx={{ color: content.borderColor, fontWeight: 600, fontSize: '0.75rem' }}
        >
          {t('dashboard.learnMore')}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background: content.gradient,
        border: `2px solid ${alpha(content.borderColor, 0.3)}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Minimize button */}
      <Box
        onClick={handleMinimize}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,1)',
          },
        }}
      >
        <Close sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{
          width: 52, height: 52, borderRadius: '8px',
          bgcolor: 'rgba(255,255,255,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {content.icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, pr: 3, color: DASH.ink }}>
            {content.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {content.subtitle}
          </Typography>
          <Button
            variant="contained"
            endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
            onClick={handleClick}
            disabled={isLoading}
            sx={{
              background: BRAND.gradient,
              color: '#fff',
              boxShadow: 'none',
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              '&:hover': {
                background: BRAND.gradientHover,
                boxShadow: 'none',
              },
            }}
          >
            {isLoading ? 'Preparing...' : content.buttonText}
          </Button>
        </Box>
      </Box>

      {/* Decorative element */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          bgcolor: alpha(content.borderColor, 0.1),
        }}
      />
    </Paper>
  );
};

export default CrossSellBanner;
